import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useStockApi } from './use-stock-api';

// Sort + dedup so callers passing the same SKUs in any order share one cache entry.
const normalizeSkus = (skus: string[] | null | undefined) =>
  skus ? Array.from(new Set(skus)).sort() : skus;

const QK_BATCH_FETCH_STOCK = (skus: string[] | null | undefined, allStockLocations: boolean) =>
  [
    'stock',
    'batch-fetch',
    normalizeSkus(skus),
    allStockLocations ? 'all-locations' : 'default',
  ] as const;

// Nullable so callers can wire conditional state without breaking rules-of-hooks.
export function useBatchFetchStock(
  skus: string[] | null | undefined,
  options?: { allStockLocations?: boolean },
) {
  const { stockApi } = useStockApi();

  const stableSkus = useMemo(() => normalizeSkus(skus), [skus]);
  const allStockLocations = options?.allStockLocations ?? false;

  return useQuery({
    queryKey: QK_BATCH_FETCH_STOCK(stableSkus, allStockLocations),
    queryFn: () => {
      if (!stableSkus?.length) {
        throw new Error('At least one SKU is required');
      }
      return stockApi.batchFetchStock({
        skus: stableSkus,
        ...(allStockLocations ? { filter: { all_stock_locations: true } } : {}),
      });
    },
    enabled: !!stableSkus?.length,
  });
}
